/**
 * ContactActions — boutons Mail · SMS · Appeler sur une fiche de l'annuaire.
 *
 * ─── LE PRINCIPE : AUCUN SECRET STOCKÉ ──────────────────────────────────────
 *
 * Le besoin (24/07) : écrire à un contact de l'annuaire — la sous-préfecture
 * pour un arrêté, un prestataire pour un devis — sans quitter VPC.
 *
 * La tentation était de stocker les identifiants SMTP de chaque agent pour
 * envoyer depuis l'application. On ne le fait PAS : un mot de passe de
 * messagerie est réutilisable (il ouvre la boîte en LECTURE), donc réversible,
 * donc à chiffrer et à protéger. VPC deviendrait responsable de la sécurité des
 * boîtes mail de toute la commune, et une base compromise donnerait accès au
 * courrier des agents. Disproportionné pour « envoyer un mail ».
 *
 * ⇒ On utilise les schémas standard `mailto:` / `sms:` / `tel:`. Le système
 * d'exploitation ouvre l'application que la personne a DÉJÀ choisie par défaut —
 * Outlook, Gmail, Thunderbird, Mail… VPC n'a pas à le savoir, et surtout pas à
 * le décider : coder « ouvrir Outlook » casserait chez qui utilise autre chose.
 *
 * Aucun paramètre à renseigner, aucune configuration à maintenir.
 *
 * ─── EFFET DE BORD UTILE : LA TRACE ─────────────────────────────────────────
 *
 * Le message part de la boîte de l'agent : il figure donc dans SES envoyés. Pour
 * un arrêté transmis à la sous-préfecture, la preuve reste côté mairie — ce qui
 * ne serait plus le cas si VPC envoyait à sa place.
 *
 * ─── DESKTOP vs MOBILE ──────────────────────────────────────────────────────
 *
 * Sur desktop, `tel:` et `sms:` déclenchent la modale système « Ouvrir avec… »,
 * interdite par la règle R13. On reprend donc le patron de `PhoneLink` :
 *   · mobile (pointer: coarse) → lien direct, ouvre l'app native ;
 *   · desktop → dialogue interne avec le numéro et un bouton Copier.
 *
 * `mailto:` ne pose pas ce problème : les navigateurs desktop savent le router.
 */
import { useState, useEffect } from 'react';
import { Mail, MessageSquare, Phone, Copy, Check } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui';

/** Retire espaces et séparateurs pour un href `tel:` / `sms:`. */
function cleanNumber(num: string): string {
  return num.replace(/[\s().-]/g, '');
}

/** Vrai sur écran tactile — même détection que PhoneLink. */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(pointer: coarse)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return isMobile;
}

interface ContactActionsProps {
  nom: string;
  email?: string | null;
  telephone?: string | null;
  /** Objet pré-rempli du mail (ex. « Demande de devis — rue du Port »). */
  sujet?: string;
  /** Corps pré-rempli du mail. */
  corps?: string;
  /** Boutons compacts (icônes seules), pour une ligne de tableau. */
  compact?: boolean;
}

export function ContactActions({
  nom, email, telephone, sujet, corps, compact = false,
}: ContactActionsProps) {
  const isMobile = useIsMobile();
  const [numDialog, setNumDialog] = useState<{ mode: 'tel' | 'sms'; num: string } | null>(null);
  const [copie, setCopie] = useState(false);

  const tel = telephone ? cleanNumber(telephone) : null;

  // encodeURIComponent sur chaque partie : un objet contenant « & » ou « = »
  // tronquerait sinon le lien.
  const mailtoHref = email
    ? `mailto:${email}` + (sujet || corps
        ? `?${[
            sujet ? `subject=${encodeURIComponent(sujet)}` : null,
            corps ? `body=${encodeURIComponent(corps)}` : null,
          ].filter(Boolean).join('&')}`
        : '')
    : null;

  async function copier(num: string) {
    try {
      await navigator.clipboard.writeText(num);
      setCopie(true);
      setTimeout(() => setCopie(false), 1500);
    } catch { /* presse-papiers indisponible : le numéro reste lisible à l'écran */ }
  }

  const cls = compact
    ? 'inline-flex h-8 w-8 items-center justify-center rounded-r-md ring-1 ring-line text-ink-muted hover:text-ink hover:bg-surface-hi'
    : 'inline-flex items-center gap-1.5 rounded-r-md px-2.5 py-1.5 text-sm ring-1 ring-line text-ink-muted hover:text-ink hover:bg-surface-hi';

  return (
    <>
      <div className="flex items-center gap-1.5">
        {mailtoHref && (
          <a href={mailtoHref} className={cls} title={`Écrire à ${nom}`} aria-label={`Écrire à ${nom}`}>
            <Mail className="h-4 w-4" aria-hidden="true" />
            {!compact && 'Mail'}
          </a>
        )}

        {tel && (isMobile ? (
          <a href={`sms:${tel}`} className={cls} title={`SMS à ${nom}`} aria-label={`SMS à ${nom}`}>
            <MessageSquare className="h-4 w-4" aria-hidden="true" />
            {!compact && 'SMS'}
          </a>
        ) : (
          <button type="button" className={cls} onClick={() => setNumDialog({ mode: 'sms', num: telephone! })}
            title={`SMS à ${nom}`} aria-label={`SMS à ${nom}`}>
            <MessageSquare className="h-4 w-4" aria-hidden="true" />
            {!compact && 'SMS'}
          </button>
        ))}

        {tel && (isMobile ? (
          <a href={`tel:${tel}`} className={cls} title={`Appeler ${nom}`} aria-label={`Appeler ${nom}`}>
            <Phone className="h-4 w-4" aria-hidden="true" />
            {!compact && 'Appeler'}
          </a>
        ) : (
          <button type="button" className={cls} onClick={() => setNumDialog({ mode: 'tel', num: telephone! })}
            title={`Appeler ${nom}`} aria-label={`Appeler ${nom}`}>
            <Phone className="h-4 w-4" aria-hidden="true" />
            {!compact && 'Appeler'}
          </button>
        ))}
      </div>

      {/* Desktop : dialogue interne (règle R13 — jamais de modale système). */}
      {numDialog && (
        <Dialog
          open
          onClose={() => setNumDialog(null)}
          title={numDialog.mode === 'tel' ? `Appeler ${nom}` : `Envoyer un SMS à ${nom}`}
        >
          <div className="space-y-4">
            <p className="text-sm text-ink-muted">
              {numDialog.mode === 'tel'
                ? 'Composez ce numéro depuis votre téléphone :'
                : 'Envoyez votre SMS à ce numéro depuis votre téléphone :'}
            </p>
            <p className="text-center font-display text-2xl font-semibold tabular-nums text-ink">
              {numDialog.num}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => void copier(numDialog.num)}>
                {copie
                  ? <><Check className="h-4 w-4" aria-hidden="true" /> Copié</>
                  : <><Copy className="h-4 w-4" aria-hidden="true" /> Copier</>}
              </Button>
              <Button variant="primary" size="sm" onClick={() => setNumDialog(null)}>Fermer</Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}
